import React from 'react';
import { Interp, css, toList } from '../../lib/render.jsx';
import Venster from '../Venster.jsx';

/**
 * Minigame: Krabbelgotchi!
 *
 * Een beestje van 16 bij 16 in de acht kleuren van het tekendoek. Het leeft door
 * terwijl de app dicht is: het wordt hongerig, het verveelt zich, er komen
 * hoopjes in zijn hok en 's nachts slaapt het. Doodgaan doet het niet.
 *
 * Een programmaatje dat je oppakt aan zijn titelbalk en neerzet waar je wil, net
 * als de Krabbellijn. Geen donkere laag eromheen: het beestje leeft door terwijl
 * je iets anders doet, dus je mag de paal eronder gewoon blijven zien.
 *
 * Het kader, de titelbalk, het wegklapstreepje en het kruisje komen uit de gedeelde
 * vensterschil (views/Venster.jsx). Er stond een eigen kopie van die balk, met een
 * geluidsknop erin; die knop is eruit. Hij zette `spelStil` om, en dat is één vlag
 * voor alle vier de spelletjes: dezelfde schakelaar staat nog in de Krabbellijn,
 * Krabbelry en Krabbelworm, en hij wordt bewaard in de browser. Je raakt dus niets
 * kwijt, en de balk houdt de twee knoppen die elk venster hier heeft.
 */
export default function Tamagotchi({ v }) {
  if (!v.showTam) return null;
  return (
    <Venster v={v} venster={v.tamVenster} titel={v.tamTitel} onSluit={v.closeTamagotchi}>
      <div style={css(v.tamPanelStyle)}>
        <div style={css(v.tamConsoleStyle)}>
          <div style={css(v.tamBoardStyle)}>
            <canvas ref={v.tamRef} width={v.tamVeld} height={v.tamVeld} role="img" aria-label="Jouw krabbelgotchi" style={css(v.tamCanvasStyle)} />
          </div>

          {/* Terwijl je een naam kiest zwijgt het venster over hoe het met hem
              gaat: dan is de vraag het enige wat er staat. */}
          {v.showTamTekst ? (
          <div style={css(v.tamTekstStyle)}>
            <div style={css(v.tamHumeurStyle)}><Interp v={v.tamHumeur} /></div>
            {/* Klik op de leeftijdregel en je kunt hem een andere naam geven. */}
            <button onClick={v.tamHernoem} title={v.tamLeeftijdTitel} style={css(v.tamLeeftijdStyle)}>
              <Interp v={v.tamLeeftijd} />
            </button>
            {/* Hier stond wie hem als laatste te eten gaf. Dat is nieuws en geen
                toestand, dus het staat nu bij de bel; zie de voermeldingen in
                logic/render-vals.js. */}
          </div>
          ) : null}

          {/* De naam staat op het schermpje; hier staan alleen de knopjes. */}
          {v.showTamNaamVeld ? (
            <div style={css(v.tamNaamRijStyle)}>
              <div style={css(v.tamNaamKopStyle)}><Interp v={v.tamNaamKop} /></div>
              <div style={css(v.tamNaamToetsRijStyle)}>
                {toList(v.tamNaamToetsen).map((t, $index) => (
                  <React.Fragment key={$index}>
                    <button onClick={t?.onClick} className={t?.klasse} title={t?.titel} aria-label={t?.titel} style={css(t?.style)}>
                      <Interp v={t?.teken} />
                    </button>
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : null}

          {v.showTamKnoppen ? (
          <div style={css(v.tamKnopRasterStyle)}>
            {toList(v.tamKnoppen).map((k, $index) => (
              <React.Fragment key={$index}>
                <button onClick={k?.onClick} disabled={k?.uit} className="gp-btn" title={k?.titel} style={css(k?.style)}>
                  <Interp v={k?.label} />
                </button>
              </React.Fragment>
            ))}
          </div>
          ) : null}

          {/* Een naam geven. Kon al door op de leeftijdregel te klikken, maar een grijs
              regeltje van elf pixels ziet er niet uit als een knop. */}
          {v.showTamNoemKnop ? (
            <button onClick={v.tamHernoem} className="gp-btn" style={css(v.tamNoemKnopStyle)}>
              <Interp v={v.tamNoemKnopLabel} />
            </button>
          ) : null}

          {/* En eronder de weg naar de tekenaar: zijn uiterlijk is iets van dit
              beestje, dus die knop hoort hier en niet alleen in het startmenu. */}
          {v.showTamUiterlijkKnop ? (
            <button onClick={v.openTamUiterlijk} className="gp-btn" style={css(v.tamNoemKnopStyle)}>
              <Interp v={v.tamUiterlijkLabel} />
            </button>
          ) : null}

          {/* De arena: kiezen tegen wie, en dan om de beurt slaan. */}
          {v.showArena ? (
            <div style={css(v.tamNaamRijStyle)}>
              <div style={css(v.arenaKopStyle)}><Interp v={v.arenaKop} /></div>

              {v.showArenaKiezer ? (
                <div style={css(v.arenaLijstStyle)}>
                  {v.showArenaLeeg ? (
                    <div style={css(v.tamMissingStyle)}><Interp v={v.arenaLeeg} /></div>
                  ) : null}
                  {v.showArenaMist ? (
                    <div style={css(v.tamMissingStyle)}><Interp v={v.arenaMistTekst} /></div>
                  ) : null}
                  {toList(v.arenaKandidaten).map((k, $index) => (
                    <React.Fragment key={$index}>
                      <button onClick={k?.onClick} disabled={k?.uit} title={k?.titel} className="gp-btn gp-plat" style={css(k?.rijStyle)}>
                        <span style={css(k?.naamStyle)}><Interp v={k?.naam} /></span>
                        <span style={css(k?.vanStyle)}><Interp v={k?.van} /></span>
                        <span style={css(k?.niveauStyle)}><Interp v={k?.niveau} /></span>
                      </button>
                    </React.Fragment>
                  ))}
                </div>
              ) : null}

              {/* Een uitdaging die op je ligt te wachten. */}
              {v.showArenaVraag ? (
                <div style={css(v.arenaAanvalRasterStyle)}>
                  <button onClick={v.neemAan} className="gp-btn pink" style={css(v.arenaKnopStyle)}>
                    Doen
                  </button>
                  <button onClick={v.stopGevecht} className="gp-btn" style={css(v.arenaKnopStyle)}>
                    Nee
                  </button>
                </div>
              ) : null}

              {v.showArenaWachten ? (
                <button onClick={v.stopGevecht} className="gp-btn" style={css(v.arenaKnopStyle)}>
                  Toch maar niet
                </button>
              ) : null}

              {v.showArenaVecht ? (
                <React.Fragment>
                  <div style={css(v.arenaLogStyle)}><Interp v={v.arenaLog} /></div>
                  <div style={css(v.arenaAanvalRasterStyle)}>
                    {toList(v.arenaAanvallen).map((a, $index) => (
                      <React.Fragment key={$index}>
                        <button onClick={a?.onClick} disabled={a?.uit} title={a?.titel} className="gp-btn" style={css(a?.style)}>
                          <Interp v={a?.naam} />
                        </button>
                      </React.Fragment>
                    ))}
                  </div>
                </React.Fragment>
              ) : null}

              {v.showArenaKlaar ? (
                <React.Fragment>
                  <div style={css(v.arenaUitslagStyle)}><Interp v={v.arenaUitslag} /></div>
                  <div style={css(v.arenaLogStyle)}><Interp v={v.arenaLog} /></div>
                  <button onClick={v.arenaOpnieuw} className="gp-btn pink" style={css(v.arenaKnopStyle)}>
                    Nog een keer
                  </button>
                </React.Fragment>
              ) : null}

              <div style={css(v.arenaStandStyle)}><Interp v={v.arenaStand} /></div>
              {v.showArenaFout ? (
                <div style={css(v.tamMissingStyle)}><Interp v={v.arenaFout} /></div>
              ) : null}
              <button onClick={v.sluitArena} className="gp-btn" style={css(v.arenaKnopStyle)}>
                Terug naar je beestje
              </button>
            </div>
          ) : null}

          {/* Op bezoek bij de buren: van wie hij is en of hij honger heeft. Zijn
              eigen naam staat in pixels op het schermpje. */}
          {v.showTamBuren ? (
            <div style={css(v.tamBuurRegelStyle)}><Interp v={v.tamBuurRegel} /></div>
          ) : null}
          {v.showTamFout ? (
            <div style={css(v.tamMissingStyle)}><Interp v={v.tamFout} /></div>
          ) : null}

          {/* Alleen als het beestje deze browser niet uit komt. */}
          {v.showTamMissing ? (
            <div style={css(v.tamMissingStyle)}><Interp v={v.tamMissingTekst} /></div>
          ) : null}
          {v.showTamVeldWeg ? (
            <div style={css(v.tamMissingStyle)}><Interp v={v.tamVeldWegTekst} /></div>
          ) : null}
        </div>
      </div>
    </Venster>
  );
}
